import Vue from 'vue'
import api, { httpErrorStatus, isRequestCancelled } from '@api/operatorWorkspace'
import { compareWorkspaceMessages } from '@components/operator-workspace/shared/utils'

export const chatKey = chat => `${chat.company_id}:${chat.id}`
export const mergeItems = (older, newer) => {
    const records = new Map(older.map(item => [`${item.kind}:${item.id}`, item]))
    newer.forEach(item => records.set(`${item.kind}:${item.id}`, item))
    return Array.from(records.values()).sort(compareWorkspaceMessages)
}
const initialState = () => ({
    companies: [],
    channels: [],
    chatStatusesBatchLimit: 0,
    inboxWindowLimit: 0,
    inboxWindowSize: 50,
    filters: { company_ids: [], channel_ids: [], view: 'all', search: '' },
    items: [],
    counters: {},
    cursor: null,
    loading: false,
    error: '',
    notices: [],
    enabled: false,
    tabs: {},
    order: [],
    activeKey: null,
    references: {},
    generation: 0,
    updatedAt: null,
})

// Tabs load independently; repeated signals share a pending request.
const inFlight = new Map()
const readStates = new WeakMap()
const detailLifetime = 3000
let tabInstance = 0

function readsFor(state) {
    if (!readStates.has(state)) readStates.set(state, {
        pending: new Map(), details: new Map(), context: null, revision: 0, fields: new WeakMap(), access: new WeakMap(),
        window: null, windowEpoch: 0, queue: null,
    })
    return readStates.get(state)
}

function nextReadRevision(state) {
    return ++readsFor(state).revision
}

function invalidateWindow(state) {
    const reads = readsFor(state)
    reads.window = null
    reads.windowEpoch++
    if (state.inboxWindowLimit && reads.queue?.generation === state.generation)
        reads.queue.next = { append: !!reads.queue.next?.append, companyIds: null }
}

function windowPage(response, known, limit) {
    const data = response?.data, meta = response?.meta
    if (Array.isArray(data?.fingerprints) && !data.fingerprints.length) data.fingerprints = {}
    const object = value => value && typeof value === 'object' && !Array.isArray(value)
    const identity = row => object(row) && [row.company_id, row.id].every(value => Number.isSafeInteger(value) && value > 0)
    if (!object(data) || !Array.isArray(data.order) || !Array.isArray(data.items) ||
        !object(data.fingerprints) || !object(data.counters) || !object(meta) ||
        !(meta.next_cursor === null || typeof meta.next_cursor === 'string') || data.order.length > limit)
        return null
    if (Object.entries(data.fingerprints).some(([id, hash]) =>
        !/^[1-9]\d*$/.test(id) || typeof hash !== 'string' || !/^[a-f0-9]{64}$/.test(hash))) return null
    const rows = new Map(), order = new Set()
    for (const row of data.items) {
        if (!identity(row) || rows.has(chatKey(row)) || !data.fingerprints[row.company_id]) return null
        rows.set(chatKey(row), row)
    }
    const items = []
    for (const row of data.order) {
        if (!identity(row) || order.has(chatKey(row))) return null
        const key = chatKey(row), fingerprint = data.fingerprints[row.company_id]
        if (!fingerprint) return null
        order.add(key)
        const item = rows.get(key) || (known?.fingerprints[row.company_id] === fingerprint && known.rows.get(key))
        if (!item) return null
        items.push(item)
    }
    if ([...rows.keys()].some(key => !order.has(key))) return null
    return { ...response, data: { ...data, items } }
}

function latestAccessRevision(state, tab) {
    const reads = readsFor(state), fields = reads.fields.get(tab)
    return Math.max(reads.access.get(tab) || 0, ...(fields ? fields.values() : []))
}

function accessRead(state, tab, generation, revision, request) {
    return request.then(response => {
        // Fresh message access must not promote the revision of cached detail fields.
        if (state.tabs[tab.key] === tab && tab.messageGeneration === generation) {
            const access = readsFor(state).access
            access.set(tab, Math.max(access.get(tab) || 0, revision))
        }
        return response
    }, error => {
        error.readRevision = revision
        throw error
    })
}

function staleReadError(state, tab, error) {
    return typeof error.readRevision === 'number' && latestAccessRevision(state, tab) > error.readRevision
}

// Server fields follow request order; message pagination has its own generation and merge rules.
function serverPatch(state, tab, patch, revision) {
    const reads = readsFor(state)
    if (!reads.fields.has(tab)) reads.fields.set(tab, new Map())
    const fields = reads.fields.get(tab), result = {}
    Object.entries(patch).forEach(([field, value]) => {
        if ((fields.get(field) || 0) > revision) return
        result[field] = value
        fields.set(field, revision)
    })
    return result
}

function cancelReads(state, accepts = () => true) {
    const reads = readsFor(state)
    reads.pending.forEach((entry, key) => {
        if (!accepts(entry)) return
        reads.pending.delete(key)
        entry.controller.abort()
    })
}

function readRequest(state, key, load, tabKey = null) {
    const reads = readsFor(state)
    if (reads.pending.has(key)) return reads.pending.get(key).promise
    const controller = new AbortController()
    const entry = { controller, tabKey, key }
    entry.promise = Promise.resolve()
        .then(() => load({ signal: controller.signal }))
        .finally(() => {
            if (reads.pending.get(key) === entry) reads.pending.delete(key)
        })
    reads.pending.set(key, entry)
    return entry.promise
}

function readDetail(state, tab, reuse = false, freshAfter = null) {
    const reads = readsFor(state)
    const generation = tab.messageGeneration
    const key = `detail:${tab.key}:${tab.instance}:${generation}`
    if (reads.pending.has(key)) return reads.pending.get(key).promise
    const cached = reads.details.get(tab.key)
    // Reuse only alongside a fresh messages GET, which still checks company/chat access on the server.
    if (
        cached?.tab === tab && cached.generation === generation &&
        ((reuse && Date.now() - cached.at < detailLifetime) || (freshAfter !== null && cached.at >= freshAfter))
    )
        return Promise.resolve(cached.detail)
    const revision = nextReadRevision(state)
    return readRequest(state, key, async options => ({
        response: await accessRead(state, tab, generation, revision, api.chat(tab, options)), revision,
    }), tab.key).then(detail => {
        if (state.tabs[tab.key] === tab && tab.messageGeneration === generation)
            reads.details.set(tab.key, { tab, generation, at: Date.now(), detail })
        return detail
    })
}

function clearTabReads(state, key) {
    cancelReads(state, entry => entry.tabKey === key)
    readsFor(state).details.delete(key)
}

const cancelled = isRequestCancelled

// crypto.randomUUID exists only in secure contexts; getRandomValues works on plain http as well.
function requestId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID()
    const bytes = window.crypto.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

// Only what defines access: the operator identity in the company and its capabilities.
// A changed name or an extra field in the payload must not close open tabs and drafts.
const companySignature = company =>
    JSON.stringify({
        id: company.company_id,
        user: company.user_company_id,
        capabilities: [...(company.capabilities || [])].sort(),
    })

// Tabs opened from a link start with only company_id and id; later fields must stay reactive.
function assignTab(tab, ...patches) {
    patches.forEach(patch => Object.keys(patch).forEach(field => Vue.set(tab, field, patch[field])))
}

// Fresh tab state for a chat that is opened for the first time.
function createTab(chat, key) {
    return {
        ...chat,
        waiting_for_reply: !!chat.waiting_for_reply,
        key,
        driverSummary: null,
        messages: [],
        previousMessages: [],
        previousCursor: null,
        previousLoaded: false,
        previousLoading: false,
        previousError: '',
        cursor: null,
        draft: '',
        file: null,
        pending: null,
        loading: false,
        sending: false,
        moving: false,
        error: '',
        archived: false,
        scrollTop: null,
        historyMode: false,
        timelineNotice: '',
        instance: ++tabInstance,
        messageGeneration: 0,
        messagesLoadedAt: null,
    }
}

function unavailableMessage(error) {
    if (error.domainCode === 'chat_taken') return 'Чат взят в работу другим оператором'
    if (httpErrorStatus(error) === 404) return 'Чат закрыт или больше недоступен'
    if (httpErrorStatus(error) === 403) return 'Доступ к чату отозван'
    return ''
}

// Open tabs outside the current queue page still get a fresh status, without loading their messages.
// At most three detail requests run at a time; every result is applied only if nothing changed meanwhile.
async function syncUnlistedTabs({ state, commit }, { generation, page, companyIds, requestedAt }) {
    const listed = new Set(page.data.items.map(chatKey))
    const remaining = state.order
        .filter(
            key =>
                !listed.has(key) &&
                !state.tabs[key].archived &&
                (!companyIds || companyIds.includes(state.tabs[key].company_id))
        )
        .map(key => state.tabs[key])
    if (state.chatStatusesBatchLimit && remaining.length)
        return syncTabStatuses({ state, commit }, remaining, generation)
    const current = tab => state.generation === generation && state.tabs[tab.key] === tab && !tab.moving
    await Promise.all(
        Array.from({ length: Math.min(3, remaining.length) }, async () => {
            while (remaining.length && state.generation === generation) {
                const tab = remaining.shift()
                try {
                    const detail = await readDetail(state, tab, false, requestedAt)
                    if (current(tab) && !tab.archived)
                        commit('tab', { key: tab.key, patch: serverPatch(state, tab, detail.response.data, detail.revision) })
                } catch (error) {
                    const message = unavailableMessage(error)
                    if (current(tab) && message && !staleReadError(state, tab, error))
                        commit('closeWithNotice', { key: tab.key, message })
                }
            }
        })
    )
}
async function syncTabStatuses({ state, commit }, tabs, generation) {
    const limit = state.chatStatusesBatchLimit
    for (let offset = 0; offset < tabs.length && state.generation === generation; offset += limit) {
        const entries = tabs.slice(offset, offset + limit).map(tab => ({ tab, generation: tab.messageGeneration }))
        const chats = {}
        entries.forEach(({ tab }) => {
            if (!chats[tab.company_id]) chats[tab.company_id] = []
            chats[tab.company_id].push(tab.id)
        })
        const revision = nextReadRevision(state)
        const response = await readRequest(state, `queue:${generation}:statuses:${offset}`, options =>
            api.chatStatuses(chats, options)
        )
        const results = new Map(response.data.items.map(item => [chatKey(item), item]))
        entries.forEach(({ tab, generation: messageGeneration }) => {
            if (state.generation !== generation || state.tabs[tab.key] !== tab ||
                tab.messageGeneration !== messageGeneration || tab.moving || tab.archived) return
            const item = results.get(tab.key)
            if (item?.status === 'available' && item.data) {
                commit('tab', { key: tab.key, patch: serverPatch(state, tab, item.data, revision) })
                // A later preview must not reuse a detail captured before this status update.
                readsFor(state).details.delete(tab.key)
            } else if (item?.status === 'unavailable' && latestAccessRevision(state, tab) <= revision) {
                const message = unavailableMessage({ code: item.error?.status, domainCode: item.error?.code })
                if (message) commit('closeWithNotice', { key: tab.key, message })
            }
        })
    }
}
// Merges a page of live messages into a tab. A gap during a busy stream or an oversized timeline
// restarts the timeline at the new page instead of showing a silent hole.
function messagesPatch(tab, page, older) {
    const merged = mergeItems(tab.messages, page.data.items)
    const oldKeys = new Set(tab.messages.map(item => `${item.kind}:${item.id}`))
    const gap =
        !older &&
        tab.messages.length &&
        page.meta.has_more &&
        !page.data.items.some(item => oldKeys.has(`${item.kind}:${item.id}`))
    const restart = !older && (merged.length > 400 || gap)
    return {
        messages: restart ? mergeItems([], page.data.items) : merged,
        historyMode: older,
        timelineNotice: restart
            ? 'Показан следующий участок переписки. Вся история доступна постранично.'
            : tab.timelineNotice,
        cursor: older || restart || !tab.messages.length ? page.meta.next_cursor : tab.cursor,
    }
}

function sendPayload(pending) {
    const payload = new FormData()
    payload.append('client_request_id', pending.id)
    if (pending.channelId) payload.append('company_platform_id', pending.channelId)
    payload.append('type', pending.file ? 'media' : 'text')
    if (pending.file) payload.append('file', pending.file)
    else payload.append('message', pending.text)
    return payload
}

// After a confirmed send the draft and file are cleared only if the operator has not changed them since.
function sentPatch(tab, pending) {
    return {
        pending: null,
        pending_send: null,
        draft: !pending.file && tab.draft === pending.text ? '' : tab.draft,
        file: tab.file === pending.file ? null : tab.file,
        ...(tab.historyMode ? { historyMode: false, messages: [], cursor: null, timelineNotice: '' } : {}),
    }
}

function sendStatusPatch(tab, pending, status) {
    const done = status === 'done'
    const failed = status === 'failed'
    return {
        pending: done || failed ? null : pending,
        ...(done || failed ? { pending_send: null } : {}),
        draft: done && !pending.file && tab.draft === pending.text ? '' : tab.draft,
        file: done && tab.file === pending.file ? null : tab.file,
        error: done
            ? ''
            : failed
            ? 'Отправка не состоялась. Можно отправить сообщение снова.'
            : 'Результат пока не подтверждён. Требуется сверка с каналом доставки.',
    }
}

function handoffNotice(reference, action, payload) {
    if (action === 'department') {
        const department = reference?.departments?.find(item => Number(item.id) === Number(payload.department_id))
        return 'Передан в очередь отдела «' + (department?.title || '№ ' + payload.department_id) + '». Оператор ещё не назначен.'
    }
    const operator = reference?.operators?.find(item => Number(item.id) === Number(payload.target_user_company_id))
    return 'Передан оператору «' + (operator?.name || '№ ' + payload.target_user_company_id) + '».'
}

// Tab fields that a successful action changes before the chat is re-read; null means nothing to patch.
function actionPatch(action, response) {
    if (action === 'finish') return { archived: true, is_mine: false }
    if (action === 'return-to-queue') return { archived: false, is_mine: false, responsible_user_id: null }
    if (action === 'assign' && response.data?.is_mine === true) return { archived: false, is_mine: true, error: '' }
    return null
}

// Legacy inbox: keep loading pages until the queue is as long as before, so a refresh does not shrink it.
async function fillInboxPages(state, generation, filters, page, revisions) {
    const target = Math.min(400, state.items.length)
    while (state.generation === generation && page.meta.next_cursor && page.data.items.length < target) {
        const nextRevision = nextReadRevision(state)
        const next = await readRequest(
            state, `queue:${generation}:inbox:${page.meta.next_cursor}`,
            options => api.inbox({ ...filters, cursor: page.meta.next_cursor }, options)
        )
        page.data.items.push(...next.data.items)
        next.data.items.forEach(item => revisions.set(chatKey(item), nextRevision))
        page.meta = next.meta
    }
}

function single(key, fn) {
    if (inFlight.has(key)) return inFlight.get(key)
    const work = Promise.resolve()
        .then(fn)
        .finally(() => inFlight.delete(key))
    inFlight.set(key, work)
    return work
}

export default {
    namespaced: true,
    state: initialState,
    getters: { active: state => state.tabs[state.activeKey] || null },
    mutations: {
        options(state, data) {
            state.channels = data.channels || []
            state.chatStatusesBatchLimit = Math.max(0, Math.min(30, Math.floor(Number(data.chat_statuses_batch_limit) || 0)))
            const windowLimit = Math.max(0, Math.min(400, Math.floor(Number(data.inbox_window_limit) || 0)))
            if (windowLimit !== state.inboxWindowLimit) invalidateWindow(state)
            if (windowLimit && !state.inboxWindowLimit)
                state.inboxWindowSize = Math.max(state.inboxWindowSize, state.items.length)
            state.inboxWindowLimit = windowLimit
        },
        context(state, companies) {
            const previous = new Map(state.companies.map(company => [company.company_id, companySignature(company)]))
            const changed = new Set(
                companies
                    .filter(
                        company =>
                            previous.has(company.company_id) &&
                            previous.get(company.company_id) !== companySignature(company)
                    )
                    .map(company => company.company_id)
            )
            if (
                JSON.stringify(state.companies.map(companySignature)) !== JSON.stringify(companies.map(companySignature))
            ) {
                cancelReads(state)
                readsFor(state).details.clear()
                readsFor(state).context = null
                state.generation++
                invalidateWindow(state)
                state.references = {}
            }
            state.companies = companies
            state.enabled = true
            const allowed = new Set(companies.map(company => company.company_id))
            state.items = state.items.filter(item => allowed.has(item.company_id))
            state.order.slice().forEach(key => {
                if (!allowed.has(state.tabs[key].company_id) || changed.has(state.tabs[key].company_id)) {
                    clearTabReads(state, key)
                    Vue.delete(state.tabs, key)
                    state.order.splice(state.order.indexOf(key), 1)
                }
            })
            if (!state.tabs[state.activeKey]) state.activeKey = state.order[0] || null
            state.filters.company_ids = state.filters.company_ids.filter(id => allowed.has(id))
            Object.keys(state.references).forEach(id => {
                if (!allowed.has(Number(id))) Vue.delete(state.references, id)
            })
        },
        filter(state, values) {
            cancelReads(state, entry => entry.key.startsWith('queue:'))
            state.filters = { ...state.filters, ...values }
            state.generation++
            invalidateWindow(state)
            state.inboxWindowSize = 50
            state.cursor = null
            state.items = []
            state.loading = false
        },
        loading(state, value) {
            state.loading = value
        },
        error(state, value) {
            state.error = value
        },
        invalidateQueue(state) {
            state.generation++
            cancelReads(state, entry => entry.key.startsWith('queue:'))
            invalidateWindow(state)
            readsFor(state).context = null
            state.loading = false
        },
        window(state, { page, limit }) {
            readsFor(state).window = {
                fingerprints: { ...page.data.fingerprints },
                rows: new Map(page.data.items.map(item => [chatKey(item), { ...item }])),
            }
            state.inboxWindowSize = limit
            if (limit >= state.inboxWindowLimit) state.cursor = null
        },
        list(state, { page, counts, append, revisions = new Map() }) {
            const records = new Map((append ? state.items : []).map(item => [chatKey(item), item]))
            page.data.items.forEach(item => {
                const key = chatKey(item)
                records.set(key, item)
                if (state.tabs[key] && !state.tabs[key].archived && !state.tabs[key].moving) {
                    assignTab(state.tabs[key], serverPatch(state, state.tabs[key], item, revisions.get(key) || nextReadRevision(state)))
                    readsFor(state).details.delete(key)
                }
            })
            state.items = Array.from(records.values()).slice(0, 400)
            state.cursor = state.items.length < 400 ? page.meta.next_cursor : null
            if (counts) state.counters = counts.data
            state.updatedAt = Date.now()
            state.error = ''
        },
        open(state, chat) {
            const key = chatKey(chat)
            if (!state.tabs[key]) {
                Vue.set(state.tabs, key, createTab(chat, key))
                state.order.push(key)
            } else if (state.tabs[key].archived) {
                // A fresh queue row reopens a chat after return or restored access.
                assignTab(state.tabs[key], chat, {
                    archived: false,
                    error: '',
                    historyMode: false,
                    timelineNotice: '',
                    previousMessages: [],
                    previousCursor: null,
                    previousLoaded: false,
                    previousLoading: false,
                    previousError: '',
                })
            }
            if (chat.preferred_channel_id) Vue.set(state.tabs[key], 'preferred_channel_id', chat.preferred_channel_id)
            state.activeKey = key
        },
        select(state, key) {
            if (state.tabs[key]) state.activeKey = key
        },
        close(state, key) {
            clearTabReads(state, key)
            Vue.delete(state.tabs, key)
            state.order = state.order.filter(item => item !== key)
            if (state.activeKey === key) state.activeKey = state.order[state.order.length - 1] || null
        },
        closeWithNotice(state, { key, message }) {
            const tab = state.tabs[key]
            if (!tab) return
            clearTabReads(state, key)
            state.notices.push('Чат № ' + tab.id + ': ' + message)
            Vue.delete(state.tabs, key)
            state.order = state.order.filter(item => item !== key)
            state.items = state.items.filter(item => chatKey(item) !== key)
            invalidateWindow(state)
            if (state.activeKey === key) state.activeKey = state.order[state.order.length - 1] || null
        },
        clearNotices(state) {
            state.notices = []
        },
        tab(state, { key, patch }) {
            const tab = state.tabs[key]
            if (!tab) return
            assignTab(tab, patch)
            if (patch.pending_send && !tab.pending)
                tab.pending = { id: patch.pending_send.client_request_id, recovered: true }
        },
        invalidateReads(state, key) {
            // Reads started before assignment or return must not restore the previous owner.
            state.generation++
            invalidateWindow(state)
            cancelReads(state, entry => entry.key.startsWith('queue:'))
            clearTabReads(state, key)
            state.loading = false
            const tab = state.tabs[key]
            if (tab) {
                tab.messageGeneration++
                tab.loading = false
                tab.previousLoading = false
            }
        },
        reference(state, { companyId, data }) {
            Vue.set(state.references, companyId, data)
        },
        reset(state) {
            cancelReads(state)
            readStates.delete(state)
            const generation = state.generation + 1
            Object.assign(state, initialState(), { generation })
        },
    },
    actions: {
        async context({ state, commit }, { maxAge = 0 } = {}) {
            const cached = readsFor(state).context
            if (state.enabled && cached && Date.now() - cached.at < maxAge) return cached.data
            const generation = state.generation
            try {
                const result = await readRequest(state, `context:${generation}`, options => api.context(options))
                if (state.generation !== generation) return null
                commit('context', result.data.companies)
                commit('options', result.data)
                readsFor(state).context = { at: Date.now(), data: result.data }
                return result.data
            } catch (error) {
                if (cancelled(error)) return null
                if (state.generation !== generation) return null
                if (httpErrorStatus(error) === 401) commit('reset')
                commit('error', error.message)
                throw error
            }
        },
        refresh({ state, dispatch }, { append = false, companyIds = null } = {}) {
            const reads = readsFor(state), generation = state.generation
            if (reads.queue?.generation === generation) {
                // Events and load-more clicks must survive an older request already in flight.
                if (companyIds || append) {
                    const next = reads.queue.next
                    reads.queue.next = {
                        append: append || !!next?.append,
                        companyIds: next?.companyIds === null || !companyIds
                            ? null : [...new Set([...(next?.companyIds || []), ...companyIds])],
                    }
                }
                return reads.queue.promise
            }
            const entry = { generation, next: { append, companyIds }, promise: null }
            entry.promise = Promise.resolve().then(async () => {
                while (entry.next && state.generation === generation) {
                    const options = entry.next
                    entry.next = null
                    try {
                        await dispatch(state.inboxWindowLimit ? 'refreshWindow' : 'refreshLegacy', options)
                    } catch (error) {
                        if (!entry.next || state.generation !== generation) throw error
                    }
                }
            }).finally(() => {
                if (reads.queue === entry) reads.queue = null
            })
            reads.queue = entry
            return entry.promise
        },
        async refreshWindow({ state, commit }, { append = false, companyIds = null } = {}) {
            if (append && !state.cursor) return
            const generation = state.generation, reads = readsFor(state), epoch = reads.windowEpoch
            const limit = Math.min(state.inboxWindowLimit, state.inboxWindowSize + (append ? 50 : 0))
            const filters = { ...state.filters, company_ids: [...state.filters.company_ids], limit }
            const requestedAt = Date.now()
            let known = reads.window
            commit('loading', true)
            try {
                for (let attempt = 0; attempt < 2; attempt++) {
                    const revision = nextReadRevision(state)
                    const response = await readRequest(state, `queue:${generation}:window:${epoch}:${attempt}`, options =>
                        api.inboxWindow({ ...filters, ...(known ? { fingerprints: known.fingerprints } : {}) }, options)
                    )
                    if (state.generation !== generation || reads.windowEpoch !== epoch) return
                    const page = windowPage(response, known, limit)
                    if (page) {
                        const revisions = new Map(page.data.items.map(item => [chatKey(item), revision]))
                        // Matching hashes confirm these exact cached fields in the freshly authorized window.
                        commit('list', { page, counts: { data: page.data.counters }, append: false, revisions })
                        commit('window', { page, limit })
                        await syncUnlistedTabs({ state, commit }, { generation, page, companyIds, requestedAt })
                        return
                    }
                    // Never partially apply an incomplete patch or label old rows as freshly loaded.
                    reads.window = null
                    known = null
                }
                throw new Error('Не удалось обновить список чатов. Повторите загрузку.')
            } catch (error) {
                if (cancelled(error) || state.generation !== generation || reads.windowEpoch !== epoch) return
                if (state.generation === generation) commit('error', error.message)
                throw error
            } finally {
                if (state.generation === generation) commit('loading', false)
            }
        },
        async refreshLegacy({ state, commit }, { append = false, companyIds = null } = {}) {
            const generation = state.generation
            const filters = {
                ...state.filters,
                company_ids: [...state.filters.company_ids],
                limit: append ? 50 : Math.min(100, Math.max(50, state.items.length)),
            }
            if (append) filters.cursor = state.cursor
            if (append && !filters.cursor) return
            return single(`queue:${generation}`, async () => {
                const requestedAt = Date.now()
                const revision = nextReadRevision(state)
                commit('loading', true)
                try {
                    const page = await readRequest(state, `queue:${generation}:inbox:${filters.cursor || ''}`, options =>
                        api.inbox({ ...filters, include_counters: append ? 0 : 1 }, options)
                    )
                    const revisions = new Map(page.data.items.map(item => [chatKey(item), revision]))
                    // Older servers still expose counters as a separate endpoint.
                    const counts = append
                        ? null
                        : page.data.counters
                        ? { data: page.data.counters }
                        : await readRequest(state, `queue:${generation}:counters`, options =>
                            api.counters(filters, options)
                        )
                    if (!append) await fillInboxPages(state, generation, filters, page, revisions)
                    if (state.generation === generation) {
                        commit('list', { page, counts, append, revisions })
                        await syncUnlistedTabs({ state, commit }, { generation, page, companyIds, requestedAt })
                    }
                } catch (error) {
                    if (cancelled(error)) return
                    if (state.generation === generation) commit('error', error.message)
                    throw error
                } finally {
                    if (state.generation === generation) commit('loading', false)
                }
            })
        },
        async open({ commit, dispatch }, chat) {
            commit('open', chat)
            await dispatch('messages', { key: chatKey(chat) })
        },
        async messages({ state, commit }, { key, older = false } = {}) {
            const tab = state.tabs[key]
            if (!tab || tab.archived || tab.moving) return
            const cursor = older ? tab.cursor : null
            if (older && !cursor) return
            const generation = tab.messageGeneration
            const current = () => state.tabs[key] === tab && tab.messageGeneration === generation
            return single(`messages:${key}:${tab.instance}:${generation}:${cursor || ''}`, async () => {
                commit('tab', { key, patch: { loading: true, error: '' } })
                try {
                    const detailRequest = readDetail(state, tab, true)
                    const revision = nextReadRevision(state)
                    const [detail, page] = await Promise.all([
                        detailRequest,
                        readRequest(
                            state, `messages:${key}:${tab.instance}:${generation}:${cursor || ''}`,
                            options => accessRead(state, tab, generation, revision, api.messages(tab, { limit: 20, cursor }, options)), key
                        ),
                    ])
                    if (!current()) return
                    const patch = serverPatch(state, tab, detail.response.data, detail.revision)
                    if (!older && tab.historyMode) {
                        commit('tab', { key, patch })
                        return
                    }
                    commit('tab', {
                        key,
                        patch: { ...patch, messagesLoadedAt: Date.now(), ...messagesPatch(tab, page, older) },
                    })
                } catch (error) {
                    if (cancelled(error)) return
                    if (!current() || tab.moving) return
                    const message = unavailableMessage(error)
                    if (message) {
                        if (staleReadError(state, tab, error)) return
                        commit('closeWithNotice', { key, message })
                        return
                    }
                    if (current()) {
                        if ([401, 403, 404].includes(httpErrorStatus(error)))
                            commit('tab', {
                                key,
                                patch: { messages: [], is_mine: false, archived: true, error: error.message },
                            })
                        else commit('tab', { key, patch: { error: error.message } })
                    }
                    throw error
                } finally {
                    if (current()) commit('tab', { key, patch: { loading: false } })
                }
            })
        },
        async earlier({ state, commit, dispatch }, key) {
            const tab = state.tabs[key]
            if (!tab || tab.loading || tab.previousLoading) return
            if (tab.cursor && !tab.archived) return dispatch('messages', { key, older: true })
            if (!tab.customer_id || (tab.previousLoaded && !tab.previousCursor)) return
            const generation = tab.messageGeneration
            const current = () => state.tabs[key] === tab && tab.messageGeneration === generation
            commit('tab', { key, patch: { previousLoading: true, previousError: '' } })
            try {
                const page = await readRequest(
                    state, `history:${key}:${tab.instance}:${generation}:${tab.previousCursor || ''}`,
                    options => api.history(tab, { limit: 30, cursor: tab.previousCursor }, options), key
                )
                if (!current()) return
                commit('tab', {
                    key,
                    patch: {
                        previousMessages: mergeItems(
                            tab.previousMessages,
                            page.data.items.map(row => ({ ...row, archived: true }))
                        ),
                        previousCursor: page.meta.next_cursor,
                        previousLoaded: true,
                    },
                })
            } catch (error) {
                if (cancelled(error)) return
                if (!current()) return
                commit('tab', {
                    key,
                    patch: {
                        previousError: error.message,
                        ...([401, 403, 404].includes(httpErrorStatus(error))
                            ? { previousMessages: [], previousCursor: null, previousLoaded: false }
                            : {}),
                    },
                })
            } finally {
                if (current()) commit('tab', { key, patch: { previousLoading: false } })
            }
        },
        async reference({ state, commit }, companyId) {
            if (!state.references[companyId]) {
                const generation = state.generation
                let response
                try {
                    response = await readRequest(state, `reference:${generation}:${companyId}`, options =>
                        api.reference(companyId, options)
                    )
                } catch (error) {
                    if (cancelled(error)) return
                    throw error
                }
                if (
                    state.generation === generation &&
                    state.companies.some(company => company.company_id === companyId)
                )
                    commit('reference', { companyId, data: response.data })
            }
        },
        async action({ state, commit, dispatch }, { key, action, payload = {} }) {
            const tab = state.tabs[key]
            if (!tab) return
            const moving = ['department', 'transfer'].includes(action)
            if (moving) {
                commit('invalidateReads', key)
                commit('tab', { key, patch: { moving: true } })
            }
            let response
            try {
                response = await api.action(tab, action, payload)
            } catch (error) {
                if (state.tabs[key] !== tab) return
                const message = unavailableMessage(error)
                if (message) {
                    commit('closeWithNotice', { key, message })
                    return
                }
                throw error
            } finally {
                if (state.tabs[key] === tab) commit('tab', { key, patch: { moving: false } })
            }
            if (state.tabs[key] !== tab) return response
            commit('invalidateReads', key)
            if (moving) {
                const message = handoffNotice(state.references[tab.company_id], action, payload)
                commit('closeWithNotice', { key, message })
            } else {
                const patch = actionPatch(action, response)
                if (patch) commit('tab', { key, patch })
            }
            // A completed handoff must not read a chat that the operator can no longer access.
            try {
                if (!['finish', 'return-to-queue', 'transfer', 'department'].includes(action)) {
                    await dispatch('messages', { key })
                }
                await dispatch('refresh')
            } catch (error) {
                commit('error', error.message)
            }
            return response
        },
        async read({ state, commit }, key) {
            const tab = state.tabs[key]
            if (!tab || !tab.is_mine || tab.archived || tab.moving || document.hidden || key !== state.activeKey) return
            const unread = tab.messages.filter(
                item => item.kind === 'message' && item.sender_id === 'customer' && !item.is_read
            )
            if (!unread.length) return
            const through = Math.max(...tab.messages.filter(item => item.kind === 'message').map(item => item.id))
            await single(`read:${tab.instance}:${key}:${through}`, () => api.action(tab, 'read', { through_message_id: through }))
            if (state.tabs[key] === tab)
                commit('tab', {
                    key,
                    patch: {
                        messages: tab.messages.map(item =>
                            item.kind === 'message' && item.id <= through ? { ...item, is_read: true } : item
                        ),
                    },
                })
        },
        async send({ state, commit, dispatch }, key) {
            const tab = state.tabs[key]
            if (!tab || tab.sending || !tab.is_mine || tab.archived || (!tab.draft.trim() && !tab.file)) return
            if (tab.pending) return dispatch('checkSend', key)
            // A retry after timeout keeps the same UUID and immutable payload.
            const pending = tab.pending || {
                id: requestId(),
                text: tab.draft,
                file: tab.file,
                channelId: tab.preferred_channel_id || tab.company_platform_id,
            }
            commit('tab', { key, patch: { sending: true, pending, error: '' } })
            let confirmed = false
            try {
                await api.action(tab, 'messages', sendPayload(pending))
                confirmed = true
                if (state.tabs[key] === tab) {
                    commit('invalidateReads', key)
                    commit('tab', { key, patch: sentPatch(tab, pending) })
                }
                await dispatch('messages', { key })
                await dispatch('refresh')
            } catch (error) {
                if (state.tabs[key] === tab)
                    commit('tab', {
                        key,
                        patch: {
                            error: error.message,
                            pending: confirmed || [400, 403, 404, 422].includes(httpErrorStatus(error)) ? null : pending,
                        },
                    })
                throw error
            } finally {
                if (state.tabs[key] === tab) commit('tab', { key, patch: { sending: false } })
            }
        },
        async checkSend({ state, commit, dispatch }, key) {
            const tab = state.tabs[key]
            if (!tab?.pending || tab.sending) return
            const pending = tab.pending
            commit('tab', { key, patch: { sending: true, error: '' } })
            try {
                const response = await api.sendStatus(tab, pending.id)
                if (state.tabs[key] !== tab) return
                if (['done', 'failed'].includes(response.data.state)) commit('invalidateReads', key)
                commit('tab', { key, patch: sendStatusPatch(tab, pending, response.data.state) })
                await dispatch('messages', { key })
            } catch (error) {
                if (state.tabs[key] === tab) commit('tab', { key, patch: { error: error.message } })
                throw error
            } finally {
                if (state.tabs[key] === tab) commit('tab', { key, patch: { sending: false } })
            }
        },
    },
}
